import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { Toast } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { HomePage } from './pages/Home';
import { AboutPage } from './pages/About';
import { ProductsPage } from './pages/Products';
import { LocationsPage } from './pages/Locations';
import { CartPage } from './pages/Cart';
import { CheckoutPage } from './pages/Checkout';
import { CheckoutSuccessPage } from './pages/CheckoutSuccess';
import { AccountPage } from './pages/Account';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { UnsubscribePage } from './pages/Newsletter';
import { FeedbackPage } from './pages/Feedback';
import { AdminPage } from './pages/Admin';
import type { Product, CartItem } from './types';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function NotFoundPage() {
  return (
    <div className="py-24">
      <div className="container-max text-center">
        <h1 className="text-6xl font-bold text-coastal-600 mb-4">404</h1>
        <p className="text-xl text-gray-600 mb-8">Page not found</p>
        <Link
          to="/"
          className="inline-block bg-coastal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-coastal-700 transition-colors"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

interface ToastMessage {
  id: number;
  text: string;
}

function App() {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('cart');
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return [];
      // Drop items persisted under an older product shape (e.g. before prices
      // moved to integer cents `priceCentsUSD`) so a stale cart never renders NaN.
      return parsed.filter(
        (i): i is CartItem =>
          i?.product &&
          typeof i.product.priceCentsUSD === 'number' &&
          Number.isFinite(i.product.priceCentsUSD) &&
          typeof i.quantity === 'number',
      );
    } catch {
      return [];
    }
  });

  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Warm the Caveat script font (used on the About page) as early as possible so
  // it's already cached by the time it's needed — no fallback-then-Caveat snap.
  // Uses the Font Loading API instead of a <link rel="preload"> so it doesn't
  // trigger Chrome's "preloaded but not used" warning on pages that don't use it.
  useEffect(() => {
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.load('500 1rem Caveat').catch(() => { /* non-critical */ });
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const showToast = useCallback((text: string) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, text }]);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleAddToCart = (product: Product, quantity: number) => {
    setCartItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        // Refresh the stored product snapshot to the latest (keeps price current).
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, product, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity }];
    });
    showToast(`${product.name} added to cart`);
  };

  const handleUpdateQuantity = (productId: string, quantity: number) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const handleRemoveItem = (productId: string) => {
    setCartItems((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const handleOrderComplete = () => {
    setCartItems([]);
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <ScrollToTop />
        <div className="relative flex flex-col min-h-screen">
          <Navigation cartCount={cartCount} />
          <main className="flex-1">
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/products" element={<ProductsPage onAddToCart={handleAddToCart} />} />
              <Route path="/locations" element={<LocationsPage />} />
              {/* Legacy route — old bookmarks/shared links still land on the page. */}
              <Route path="/farmers-markets" element={<Navigate to="/locations" replace />} />
              <Route path="/cart" element={
                <CartPage
                  items={cartItems}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveItem={handleRemoveItem}
                />
              } />
              <Route path="/checkout" element={
                <CheckoutPage items={cartItems} onOrderComplete={handleOrderComplete} />
              } />
              <Route path="/success" element={<CheckoutSuccessPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/newsletter/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </ErrorBoundary>
          </main>
          <Footer />
        </div>
        <Toast toasts={toasts} onDismiss={dismissToast} />
      </AuthProvider>
    </Router>
  );
}

export default App;
